/**
 * When "Draw the cards" is offered, and when it is ready.
 *
 * The interview is the Mystic's, but the cards are the seeker's to ask for.
 * Drawing is READY at the same point the reader's own readiness gate offers
 * the cards. Pace is how many follow-up questions the Mystic asks after the
 * seeker's opening question (quick 0, balanced 1, patient 2), so the cards are
 * ready once the seeker has sent follow-ups + 1 messages, the opening question
 * included. The backend sends that number on the reader detail
 * (`interview_answers_before_cards`, from reader/service/interview_tempo.py
 * answers_before_cards); ANSWERS_BEFORE_CARDS is the fallback when it is
 * missing and must match it. Once the reader itself offers Select Cards, that
 * offer folds INTO the one "Draw the cards" tool chip (2026-10-27), so drawing
 * has one home: the chip row under the conversation, never the nav as well.
 */
const INTERVIEW_STATES = new Set(['INTERVIEW', 'TAROT_FUNNEL'])
export const ANSWERS_BEFORE_CARDS = { quick: 1, balanced: 2, patient: 3 }

/** Seeker messages before the cards are ready: the backend's number, else the table. */
export function answersBeforeCards ({ pace, answersBeforeCards } = {}) {
  const fromBackend = Number(answersBeforeCards)
  if (answersBeforeCards !== null && answersBeforeCards !== '' &&
      Number.isInteger(fromBackend) && fromBackend > 0) return fromBackend
  return ANSWERS_BEFORE_CARDS[pace] ?? ANSWERS_BEFORE_CARDS.balanced
}

export function drawCardsOffered ({ state } = {}) {
  return INTERVIEW_STATES.has(state)
}

/** The reader's own Select Cards among its buttons, if it offered one. */
export function readerSelectCards (actions) {
  const list = Array.isArray(actions) ? actions : []
  return list.find((a) => a && a.action_type === 'select_cards') || null
}

/** The reader's buttons minus Select Cards while the composer button owns it. */
export function withoutSelectCards (actions, state) {
  const list = Array.isArray(actions) ? actions : []
  if (!INTERVIEW_STATES.has(state)) return list
  return list.filter((a) => !(a && a.action_type === 'select_cards'))
}

// `busy`: the reader is still answering (round-trip or stream in flight).
// Drawing then let the reply land mid-pick — a stray question under the first
// card with the card's teaser glued onto it. The reply finishes; then the cards.
// `readerOffers`: the reader has offered Select Cards itself, which outranks
// the count (the reader is the one who set it).
export function drawCardsReady ({ pace, answersBeforeCards: needed, seekerTurns = 0, busy = false, readerOffers = false } = {}) {
  return !busy && (readerOffers || seekerTurns >= answersBeforeCards({ pace, answersBeforeCards: needed }))
}

/**
 * Chips are tools (Chris, 2026-10-27): drawing is a tool chip under the
 * conversation, "Draw the cards", shown only once the cards are ready. Before
 * that there is nothing to tap and nothing to explain (no "Tell me a little
 * first"). The reader's own Select Cards, when it offered one, is the action.
 */
export const DRAW_LABEL = 'Draw the cards'

export function drawCardsChip ({ offered = false, ready = false, readerAction = null, state = '' } = {}) {
  if (!offered || !ready) return null
  return { ...(readerAction || drawCardsAction(state)), button_label: DRAW_LABEL }
}

/** The action itself, shaped like the reader's own Select Cards chip. */
export function drawCardsAction (state) {
  return { key: 'select_cards', action_type: 'select_cards', button_label: DRAW_LABEL,
           conversation_type: state === 'INTERVIEW' ? 'INTERVIEW' : 'TAROT_FUNNEL' }
}
